import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
  type TooltipItem,
} from "chart.js";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Bar } from "react-chartjs-2";

import {
  ChartPanel,
  DashboardsPageFrame,
  DataTable,
  FilterToggle,
  HorizonTabs,
  KpiCard,
  KpiGrid,
} from "./dashboardKit";
import { useTheme } from "../../hooks/useTheme";
import { AiEngineeringPage } from "./AiEngineeringPage";
import { type EngVelocityView, isEngVelocityView } from "./aiEngineeringTypes";
import { goalLinearRow, goalLocRow, goalPrRow } from "./engVelocityGoal";
import { aiEngineeringQueryOptions, engVelocityQueryOptions } from "./dashboardsReactQuery";
import type {
  EngVelocityLocByRepoRow,
  EngVelocityLocRow,
  EngVelocitySnapshotResponse,
  EngVelocityTimeline,
} from "./engVelocityTypes";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const INK = "#242124";
const INK_DARK = "#FFFFFF";
const MUTED = "#6D6A66";
const GRID = "#EDE9E4";
const ADD = "#2f9e57";
const DEL = "#d1495b";
const OPERATOR_ADD = "#3b82f6";
const OPERATOR_DEL = "#2563eb";

const PERSON: Record<string, { color: string; label: string }> = {
  beau: { label: "Beau", color: "#0e7c6b" },
  mateus: { label: "Mateus", color: "#4f5bd5" },
  jacob: { label: "Jacob", color: "#c77d29" },
  vihar: { label: "Vihar", color: "#d97706" },
  igor: { label: "Igor", color: "#b0476b" },
  jon: { label: "Jon", color: "#2c7a9b" },
  phil: { label: "Phil", color: "#7c5cbd" },
  max: { label: "Max", color: "#7379c9" },
  meraj: { label: "Meraj", color: "#a33588" },
  jill: { label: "Jill", color: "#8a6fd1" },
  "cm-operators": { label: "Operators not attributed", color: "#6b7280" },
  codex: { label: "Codex", color: "#9aa2b1" },
  dependabot: { label: "dependabot", color: "#b8bfc9" },
  coderabbit: { label: "coderabbit", color: "#c9cdd6" },
};

const TEAM_USERS = [
  "mateus",
  "beau",
  "igor",
  "jacob",
  "vihar",
  "jon",
  "phil",
  "max",
  "meraj",
  "cm-operators",
] as const;

const STACK_ORDER = [...TEAM_USERS];

const REPO_OPTIONS = [
  { value: "all", label: "All" },
  { value: "web", label: "Web" },
  { value: "operators", label: "Operators" },
  { value: "operators_dashboards", label: "Operators dashboards" },
  { value: "marketing-site", label: "Marketing site" },
  { value: "desktop", label: "Desktop" },
] as const;

function personLabel(user: string): string {
  return PERSON[user]?.label ?? user;
}

function personColor(user: string): string {
  return PERSON[user]?.color ?? "#b8bfc9";
}

function fmt(n: number): string {
  return Number(n).toLocaleString("en-US");
}

function shortK(n: number): string {
  if (n >= 1000) {
    return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  }
  return String(n);
}

function monthDay(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${Number(m)}/${Number(d)}`;
}

function sum<T>(rows: T[], key: keyof T): number {
  return rows.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);
}

const SNAPSHOT_MAX_AGE_MS = 48 * 60 * 60 * 1000;

function snapshotAgeLabel(generatedAt: string): string {
  const timestamp = Date.parse(generatedAt);
  if (!Number.isFinite(timestamp)) {
    return "age unavailable";
  }

  const hours = Math.max(0, Math.floor((Date.now() - timestamp) / 3600000));
  if (hours < 1) {
    return "less than 1 hour ago";
  }
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }

  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

function isSnapshotStale(generatedAt: string): boolean {
  const timestamp = Date.parse(generatedAt);
  return !Number.isFinite(timestamp) || Date.now() - timestamp > SNAPSHOT_MAX_AGE_MS;
}

const chartBase = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
};

function LocPanel(props: {
  loc: EngVelocitySnapshotResponse["loc"];
  locByRepo?: EngVelocitySnapshotResponse["locByRepo"];
}) {
  const { resolvedTheme } = useTheme();
  const [mode, setMode] = useState<"source" | "all">("source");
  const [repo, setRepo] = useState<string>("all");
  const scopedRows: Array<EngVelocityLocRow | EngVelocityLocByRepoRow> =
    repo === "all"
      ? props.loc[mode]
      : (props.locByRepo?.[mode] ?? []).filter((row) => row.repo === repo);
  const byUser = new Map(scopedRows.map((row) => [row.user, row]));
  const teamRows = TEAM_USERS.map((user) => ({
    user,
    additions: 0,
    deletions: 0,
    operatorAdditions: 0,
    operatorDeletions: 0,
    ...byUser.get(user),
  }));
  const rows = [...teamRows, goalLocRow(teamRows)].sort(
    (a, b) => b.additions + b.deletions - (a.additions + a.deletions),
  );

  const data = {
    labels: rows.map((r) => personLabel(r.user)),
    datasets: [
      {
        label: "Direct additions",
        data: rows.map((r) => r.additions - r.operatorAdditions),
        backgroundColor: ADD,
        borderRadius: 3,
        stack: "loc",
      },
      {
        label: "Operators additions",
        data: rows.map((r) => r.operatorAdditions),
        backgroundColor: OPERATOR_ADD,
        borderRadius: 3,
        stack: "loc",
      },
      {
        label: "Direct deletions",
        data: rows.map((r) => -(r.deletions - r.operatorDeletions)),
        backgroundColor: DEL,
        borderRadius: 3,
        stack: "loc",
      },
      {
        label: "Operators deletions",
        data: rows.map((r) => -r.operatorDeletions),
        backgroundColor: OPERATOR_DEL,
        borderRadius: 3,
        stack: "loc",
      },
    ],
  };

  const options = {
    ...chartBase,
    indexAxis: "y" as const,
    scales: {
      x: {
        stacked: true,
        grid: { color: GRID },
        ticks: {
          color: MUTED,
          font: { size: 11 },
          callback: (v: string | number) => shortK(Math.abs(Number(v))),
        },
      },
      y: {
        stacked: true,
        grid: { display: false },
        ticks: { color: resolvedTheme === "dark" ? INK_DARK : INK, font: { size: 12 } },
      },
    },
    plugins: {
      ...chartBase.plugins,
      tooltip: {
        callbacks: {
          label: (ctx: TooltipItem<"bar">) =>
            `${ctx.dataset.label ?? ""}: ${fmt(Math.abs(Number(ctx.raw)))}`,
        },
      },
    },
  };

  return (
    <ChartPanel
      title="Lines changed by engineer"
      action={
        <div className="flex flex-wrap gap-1.5">
          <FilterToggle active={mode === "source"} onClick={() => setMode("source")}>
            Source
          </FilterToggle>
          <FilterToggle active={mode === "all"} onClick={() => setMode("all")}>
            All files
          </FilterToggle>
        </div>
      }
    >
      <p className="mb-2 text-xs text-muted-foreground">
        {mode === "source"
          ? "Source excludes generated & vendored files (drizzle migrations, schema.sql, generated types, lockfiles, minified bundles, snapshots). Green/red is direct work; blue is work attributed from Operators PRs."
          : "All files — includes generated & vendored artifacts."}{" "}
        Unattributed Operators work stays in its catchall row.
      </p>
      <fieldset className="mb-2 flex flex-wrap gap-1.5 border-0 p-0" aria-label="Repository">
        {REPO_OPTIONS.map((option) => (
          <FilterToggle
            key={option.value}
            active={repo === option.value}
            onClick={() => setRepo(option.value)}
          >
            {option.label}
          </FilterToggle>
        ))}
      </fieldset>
      <div className="mb-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
        {(
          [
            ["Direct additions", ADD],
            ["Operators-attributed LOC", OPERATOR_ADD],
            ["Direct deletions", DEL],
          ] as const
        ).map(([label, color]) => (
          <span key={label} className="inline-flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="size-2.5 rounded-sm"
              style={{ background: color }}
            />
            {label}
          </span>
        ))}
      </div>
      <div className="relative h-80">
        <Bar data={data} options={options} />
      </div>
    </ChartPanel>
  );
}

function TimelinePanel(props: { timeline: EngVelocityTimeline }) {
  const { days, byDay } = props.timeline;
  const active = STACK_ORDER.filter((u) => days.some((d) => byDay[d]?.[u]));
  const data = {
    labels: days.map(monthDay),
    datasets: active.map((user) => ({
      label: personLabel(user),
      data: days.map((d) => byDay[d]?.[user] || 0),
      backgroundColor: personColor(user),
    })),
  };
  const options = {
    ...chartBase,
    plugins: {
      ...chartBase.plugins,
      legend: {
        display: true,
        position: "bottom" as const,
        labels: { color: MUTED, font: { size: 11 }, boxWidth: 12 },
      },
      tooltip: { mode: "index" as const },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: {
          color: MUTED,
          font: { size: 10 },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 12,
        },
      },
      y: {
        stacked: true,
        grid: { color: GRID },
        ticks: { color: MUTED, font: { size: 11 }, precision: 0 },
      },
    },
  };

  return (
    <ChartPanel title="Commit timeline — daily, stacked by engineer">
      <div className="relative h-64">
        <Bar data={data} options={options} />
      </div>
    </ChartPanel>
  );
}

function personCell(user: string) {
  return (
    <span className="inline-flex items-center gap-2">
      <span
        className="size-2.5 shrink-0 rounded-full"
        style={{ background: personColor(user) }}
        aria-hidden="true"
      />
      {personLabel(user)}
    </span>
  );
}

function EngVelocityContent(props: { data: EngVelocitySnapshotResponse }) {
  const { generatedAt, window: win, loc, locByRepo, prs, linear, timeline, freshness } = props.data;

  const commitTotal = sum(loc.all, "commits");
  const sourceAdded = sum(loc.source, "additions");
  const rawAdded = sum(loc.all, "additions");
  const prsMerged = sum(prs, "merged");
  const prsOpened = sum(prs, "opened");
  const deployTotal = sum(linear, "deployedToProd");

  const visibleUsers = new Set<string>(TEAM_USERS);
  const teamPrRows = prs.filter((row) => visibleUsers.has(row.user));
  const prRows = [...teamPrRows, goalPrRow(teamPrRows)].sort((a, b) => b.merged - a.merged);
  const teamLinearRows = linear.filter((row) => visibleUsers.has(row.user));
  const linearRows = [...teamLinearRows, goalLinearRow(teamLinearRows)].sort(
    (a, b) => b.deployedToProd - a.deployedToProd,
  );

  const freshnessNote =
    freshness.prs === "live" || freshness.linear === "live"
      ? "PRs & Linear live · LOC and timeline from the nightly snapshot"
      : "All metrics from the nightly snapshot";
  const snapshotStale = isSnapshotStale(generatedAt);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">
        {win.label} ({win.since} → {win.until}) · {freshnessNote}. LOC is a volume signal, not a
        quality metric. Snapshot generated {snapshotAgeLabel(generatedAt)}.
      </p>
      {snapshotStale ? (
        <p className="rounded-md border border-amber-300/70 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          This snapshot has an invalid timestamp or is more than 48 hours old. LOC and timeline may
          be stale; check the snapshot refresh workflow.
        </p>
      ) : null}

      <KpiGrid>
        <KpiCard label="Commits" value={fmt(commitTotal)} footnote="non-merge · 4 repos" />
        <KpiCard
          label="Source lines added"
          value={shortK(sourceAdded)}
          footnote={`${shortK(rawAdded)} incl. generated`}
        />
        <KpiCard label="PRs merged" value={fmt(prsMerged)} footnote={`${fmt(prsOpened)} opened`} />
        <KpiCard label="Deployed to prod" value={fmt(deployTotal)} footnote="Linear tickets" />
      </KpiGrid>

      <LocPanel loc={loc} locByRepo={locByRepo} />

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <ChartPanel title="Pull requests">
          <DataTable
            columns={[
              {
                key: "user",
                label: "Engineer",
                render: (r) => personCell(r.user),
              },
              { key: "opened", label: "Opened", align: "right" },
              { key: "merged", label: "Merged", align: "right" },
              { key: "closedUnmerged", label: "Closed", align: "right" },
            ]}
            rows={prRows}
            rowKey={(r) => r.user}
          />
        </ChartPanel>

        <ChartPanel title="Deployed to production (by assignee)">
          <DataTable
            columns={[
              {
                key: "user",
                label: "Assignee",
                render: (r) => personCell(r.user),
              },
              { key: "deployedToProd", label: "Tickets", align: "right" },
            ]}
            rows={linearRows}
            rowKey={(r) => r.user}
          />
        </ChartPanel>
      </div>

      <TimelinePanel timeline={timeline} />
    </div>
  );
}

export function EngVelocityDashboard(props: {
  onViewChange?: (view: EngVelocityView) => void;
  view?: EngVelocityView;
}) {
  const view = props.view ?? "velocity";
  const query = useQuery({ ...engVelocityQueryOptions(), enabled: view === "velocity" });
  const aiEngineeringQuery = useQuery({
    ...aiEngineeringQueryOptions(),
    enabled: view === "ai-engineering",
  });

  function handleViewChange(value: string) {
    if (isEngVelocityView(value)) {
      props.onViewChange?.(value);
    }
  }

  return (
    <DashboardsPageFrame className="flex flex-col gap-3">
      <div>
        <h1 className="text-xl font-bold tracking-tight text-foreground">
          {view === "ai-engineering" ? "AI Engineering" : "Eng Velocity"}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {view === "ai-engineering"
            ? "An evidence-led view of AI-assisted engineering: completed work, reviewable change, validated code, and flow over time."
            : "Commits, source lines, pull requests, and production deploys per engineer across web, marketing-site, operators, operators_dashboards & desktop."}
        </p>
      </div>

      <HorizonTabs
        idPrefix="eng-velocity"
        options={[
          { label: "Eng Velocity", value: "velocity" },
          { label: "AI Engineering", value: "ai-engineering" },
        ]}
        value={view}
        onChange={handleViewChange}
      />

      {view === "ai-engineering" && aiEngineeringQuery.isPending ? (
        <div className="rounded-lg border border-border/70 bg-background/80 px-4 py-8 text-sm text-muted-foreground">
          Loading AI Engineering telemetry…
        </div>
      ) : null}

      {view === "ai-engineering" && aiEngineeringQuery.isError ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-8 text-sm text-destructive">
          {aiEngineeringQuery.error instanceof Error
            ? aiEngineeringQuery.error.message
            : "Failed to load AI Engineering telemetry"}
        </div>
      ) : null}

      {view === "ai-engineering" && aiEngineeringQuery.data ? (
        <AiEngineeringPage data={aiEngineeringQuery.data} />
      ) : null}

      {view === "velocity" && query.isPending ? (
        <div className="rounded-lg border border-border/70 bg-background/80 px-4 py-8 text-sm text-muted-foreground">
          Loading Eng Velocity…
        </div>
      ) : null}

      {view === "velocity" && query.isError ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-8 text-sm text-destructive">
          {query.error instanceof Error ? query.error.message : "Failed to load Eng Velocity"}
        </div>
      ) : null}

      {view === "velocity" && query.data ? <EngVelocityContent data={query.data} /> : null}
    </DashboardsPageFrame>
  );
}
